import parse, { DomElement, domToReact } from "html-react-parser";
import React, { ReactElement } from "react";
import InlineCountdown from "./ContentBlocks/InlineCountdown";
import IconTextLink from "./ContentBlocks/IconTextLink";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import ImageTextLayout from "./ContentBlocks/ImageText";
import InfoBanner from "./ContentBlocks/InfoBanner";
import PodcastEpisode from "./ContentBlocks/PodcastEpisode";
import ProfileBlock from "./ContentBlocks/ProfileBlock";
import { SpoilerImage } from "./ContentBlocks/SpoilerImage";
import { GalleryBlock } from "./ContentBlocks/GalleryBlock";
import { ShopProduct } from "./ContentBlocks/ShopProduct";
import { ShopSection } from "./ContentBlocks/ShopSection";
import { FacebookEmbed } from "./ContentBlocks/FacebookEmbed";
import { FeaturedProduct } from "./ContentBlocks/FeaturedProduct";

interface IProps {
  htmlString?: string | null;
}

const ContentParser: React.FC<IProps> = ({ htmlString }) => {
  const options = {
    replace: (node: DomElement) => {
      switch (node.name) {
        case "link":
        case "style":
          return <div />;
        case "facebookembed":
          if (node.attribs) {
            return (
              <FacebookEmbed
                link={node.attribs["data-link"]}
              />
            );
          }
          return <p>Could not render Facebook embed</p>;
        case "inlinecountdown":
          if (node.attribs) {
            return (
              <InlineCountdown
                date={node.attribs["data-countdown-date"]}
                image={node.attribs["data-image"]}
                text={node.attribs["data-countdown-text"]}
                linkUrl={node.attribs["data-link-url"]}
                linkText={node.attribs["data-link-text"]}
              />
            );
          }
          return <p>Could not render inline countdown</p>;
        case "icontextlink":
          if (node.attribs) {
            return (
              <IconTextLink
                icon={node.attribs["data-icon"] as FontAwesomeIconProps["icon"]}
                iconSet={node.attribs["data-icon-set"] as "fab" | "fas"}
                text={decodeURIComponent(
                  (node.attribs["data-text"] + "").replace(/\+/g, "%20")
                )}
                linkUrl={node.attribs["data-link-url"]}
                linkText={node.attribs["data-link-text"]}
              />
            );
          }
          return <p>Could not render Icon & Text link</p>;
        case "spoilerimage":
          if (node.attribs) {
            return <SpoilerImage image={node.attribs["data-image"]} />;
          }
          return <p>Could not render spoiler image</p>;
        case "shopsection":
          if (node.attribs) {
            return (
              <ShopSection
                name={node.attribs["data-name"]}
                coverImage={node.attribs["data-cover-image"]}
              />
            );
          }
          return <p>Could not render shop section</p>;
        case "shopproduct":
          if (node.attribs) {
            return (
              <ShopProduct
                name={node.attribs["data-name"]}
                image={node.attribs["data-image"]}
                productUrl={node.attribs["data-product-url"]}
                description={node.attribs["data-description"]}
              />
            );
          }
          return <p>Could not render shop product</p>;
        case "galleryblock":
          if (node.attribs) {
            return (
              <GalleryBlock
                coverImage={node.attribs["data-cover-image"]}
                images={[
                  node.attribs["data-image-1"],
                  node.attribs["data-image-2"],
                  node.attribs["data-image-3"],
                  node.attribs["data-image-4"],
                  node.attribs["data-image-5"],
                  node.attribs["data-image-6"],
                  node.attribs["data-image-7"],
                  node.attribs["data-image-8"],
                  node.attribs["data-image-9"],
                  node.attribs["data-image-10"],
                ]}
              />
            );
          }
          return <p>Could not render gallery block</p>;
        case "imagetextlayout":
          if (node.attribs) {
            return (
              <ImageTextLayout
                image={node.attribs["data-image"]}
                textArea={decodeURIComponent(
                  (node.attribs["data-text-area"] + "").replace(/\+/g, "%20")
                )}
                layout={
                  node.attribs["data-layout"] as
                  | "right"
                  | "left"
                  | "top"
                  | "bottom"
                }
              />
            );
          }
          return <p>Could not render image text layout</p>;
        case "infobanner":
          if (node.attribs) {
            return (
              <InfoBanner
                bannerType={node.attribs["data-banner-type"]}
                icon={node.attribs["data-icon"] as FontAwesomeIconProps["icon"]}
                iconSet={node.attribs["data-icon-type"] as "fab" | "fas"}
                text={decodeURIComponent(
                  (node.attribs["data-text"] + "").replace(/\+/g, "%20")
                )}
                bannerTitle={node.attribs["data-banner-title"]}
              />
            );
          }
          return <p>Could not render info banner</p>;
        case "profileblock":
          if (node.attribs) {
            return (
              <ProfileBlock
                image={node.attribs["data-image"]}
                layout={
                  node.attribs["data-layout"] as
                  | "right"
                  | "left"
                  | "top"
                  | "bottom"
                }
                title={node.attribs["data-title"]}
                subHeading={node.attribs["data-sub-heading"]}
                textArea={decodeURIComponent(
                  (node.attribs["data-text-area"] + "").replace(/\+/g, "%20")
                )}
                occupation={node.attribs["data-occupation"]}
                status={node.attribs["data-status"]}
              />
            );
          }
          return <p>Could not render profile block</p>;
        case "podcastepisode":
          if (node.attribs) {
            return (
              <PodcastEpisode
                episodeNumber={node.attribs["data-episode-number"]}
                title={decodeURIComponent(
                  (node.attribs["data-title"] + "").replace(/\+/g, "%20")
                )}
                info={decodeURIComponent(
                  (node.attribs["data-info"] + "").replace(/\+/g, "%20")
                )}
                releaseDate={node.attribs["data-release-date"]}
                episodeLength={node.attribs["data-length"]}
                youtubeLink={node.attribs["data-youtube-link"]}
                itunesLink={node.attribs["data-itunes-link"]}
                podbeanLink={node.attribs["data-podbean-link"]}
                stitcherLink={node.attribs["data-stitcher-link"]}
              />
            );
          }
          return <p>Could not render podcast episode</p>;
        case "featuredproduct":
          if (node.attribs) {
            return (
              <FeaturedProduct
                productUrl={node.attribs["data-product-url"]}
                bannerImage={node.attribs["data-banner-image"]}
                name={node.attribs["data-name"]}
                image={node.attribs["data-image"]}
                price={node.attribs["data-price"]}
                infoFooter={node.attribs["data-info-footer"]}
                underPrice={node.attribs["data-under-price"]}
                description={node.attribs["data-description"]}

              />
            );
          }
          return <p>Could not render podcast episode</p>;
        default:
          // Default - just return the input
          return;
      }
    },
  };

  if (htmlString) {
    return parse(htmlString, options) as ReactElement;
  } else {
    return null;
  }
};

export default ContentParser;
